import { Clock, FileQuestion, Shuffle, Archive, EyeOff } from "lucide-react";
import { Reveal, Chapter } from "../components/Reveal";

const ITEMS = [
  {
    icon: Clock,
    title: "Delayed responses",
    text: "Enquiries can sit unanswered for hours while the team is with patients in the chair.",
  },
  {
    icon: FileQuestion,
    title: "Quotes that go cold",
    text: "Treatment quotes are often sent once, then never revisited — even when the patient was genuinely interested.",
  },
  {
    icon: Shuffle,
    title: "Inconsistent follow-up",
    text: "Follow-up often depends on who happens to be at the desk and how busy the day has been.",
  },
  {
    icon: Archive,
    title: "Old leads left untouched",
    text: "Many practices hold months of past enquiries that have never been properly re-engaged.",
  },
  {
    icon: EyeOff,
    title: "Unclear visibility",
    text: "It can be hard to see where enquiries are being lost between first contact and a booked consultation.",
  },
];

export default function Problem() {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="scroll-mt-24 border-t border-ink/10 py-24 md:py-32"
    >
      <div className="mx-auto max-w-6xl px-6">
        <div className="max-w-3xl">
          <Reveal>
            <Chapter number="01" label="The Problem" />
          </Reveal>
          <Reveal delay={60}>
            <h2 className="font-display text-3xl font-medium leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Enquiries don&apos;t always get the response they deserve
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-6 text-base leading-relaxed text-ink/70 md:text-lg">
              Enquiries arrive through the phone, the website, WhatsApp and
              email — often while the same team is welcoming patients, managing
              the diary and running the practice floor. In that environment,
              even well-run practices can find that some enquiries are handled
              less consistently than they&apos;d like.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-x-12 gap-y-10 md:grid-cols-2">
          {ITEMS.map((item, i) => (
            <Reveal key={item.title} delay={i * 100}>
              <div data-testid={`problem-item-${i}`} className="flex gap-5">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-soft">
                  <item.icon size={20} className="text-teal" strokeWidth={1.8} />
                </div>
                <div>
                  <h3 className="font-medium tracking-wide text-ink">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink/65">{item.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={200}>
          <p
            data-testid="problem-closing-line"
            className="mt-16 max-w-3xl border-l-2 border-teal/40 pl-5 text-base italic leading-relaxed text-ink/70 md:text-lg"
          >
            None of this means a practice is doing anything wrong. It&apos;s
            simply what tends to happen when enquiry volume grows faster than
            the systems for handling it.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
